import React, { useEffect, useState } from 'react';
import FormButton from 'components/FormButton/FormButton';
import My5LightLogo from 'assets/icons/My5LightLogo';
import ParamountLightLogo from 'assets/icons/ParamountLightLogo';
import {
    MarketingConsentModalConfig,
    MarketingConsentState,
    saveMarketingConsent,
    toggleMarketingConsent,
} from './MarketingConsent.helper';

import './MarketingConsent.scss';

interface Props {
    open: boolean;
    onDone: () => void;
    config?: MarketingConsentModalConfig;
}

const MarketingConsent: React.FC<Props> = ({ open, onDone, config }) => {
    const [consent, setConsent] = useState<MarketingConsentState>({
        channel5: false,
        viacom: false,
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setConsent({ channel5: false, viacom: false });
        }
    }, [open]);

    const onToggle = (scope: 'channel5' | 'viacom') => {
        setConsent((prevState) => toggleMarketingConsent(prevState, scope));
    };

    const onConfirm = async () => {
        setIsSaving(true);
        await saveMarketingConsent(consent);
        setIsSaving(false);
        onDone();
    };

    const onSkip = async () => {
        setIsSaving(true);
        await saveMarketingConsent();
        setIsSaving(false);
        onDone();
    };

    if (!open || !config) {
        return null;
    }
    const policyUrl = config.modalpolicyUrl.startsWith('http')
        ? config.modalpolicyUrl
        : `https://${config.modalpolicyUrl}`;
    const consentParties = [
        {
            key: 'channel5' as const,
            checked: consent.channel5,
            copy: config.Channel5AndMy5Text,
            logo: <My5LightLogo width={30} height={35} />,
        },
        {
            key: 'viacom' as const,
            checked: consent.viacom,
            copy: config.viacomcbsText,
            logo: <ParamountLightLogo height={40} width={130} />,
        },
    ];

    return (
        <div className="marketing-consent-overlay">
            <div className="marketing-consent-modal">
                <div className="marketing-consent-body">
                    <h1 className="marketing-consent-title">{config.modalTitle}</h1>
                    <p className="marketing-consent-copy">{config.modalCopy}</p>

                    {consentParties.map((party) => (
                        <section key={party.key} className="consent-party-card">
                            <div className="selection-row">
                                {party.logo}
                                <label className="switch" aria-label={`toggle-${party.key}`}>
                                    <input
                                        type="checkbox"
                                        checked={party.checked}
                                        onChange={() => onToggle(party.key)}
                                        disabled={isSaving}
                                    />
                                    <span className="slider" />
                                </label>
                            </div>
                            <p className="consent-content">{party.copy}</p>
                        </section>
                    ))}

                    <section className="marketing-consent-actions">
                        <FormButton
                            buttonType="button"
                            buttonId="skip-marketing-consent"
                            buttonText={config.communicationButton2}
                            type="ghost"
                            disabled={isSaving}
                            onClick={onSkip}
                        />
                        <FormButton
                            buttonType="button"
                            buttonId="confirm-marketing-consent"
                            buttonText={config.communicationButton1}
                            disabled={isSaving || (!consent.channel5 && !consent.viacom)}
                            onClick={onConfirm}
                        />
                    </section>
                </div>
                <div className="marketing-consent-footer">
                    <div className="consent-policy">
                        {config.modalpolicyFooterText || ''}
                        <a href={policyUrl} target="_blank" rel="noopener noreferrer nofollow" className="link">
                            {config.modalpolicyLink}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarketingConsent;
